// The account panel each page shows, and which one a role gets on pages that are not a board.
//
// Since go-live every role has one accounts tab (`lib/viewTabs.ts`): My Accounts for a strategist
// (the board on `/execution`, on their own accounts, `scopes_to_self`) and for an AE (their book on
// `/ae`); Strategist Accounts for admin, leadership and viewers (the board, every strategist's, with
// the Strategist dropdown).
// The book payload is the same for every role either way, so this is a view, not a grant.
//
// The panel goes with the page, not the person (20 Oct review of #601):
// - `board`: the strategist's panel, Outreach and all, on `/execution`.
// - `ae`: the AE's own book on `/ae` (GRO-360), still the AE's landing page and reachable by URL.
// - `oversight`: the read-only panel. Its page (`/accounts`) now redirects to the board; the panel
//   is kept for leadership and viewers on All accounts, as before.
// Each panel decides `companyInfo` (the Company info tab), `factStrip` (the facts above every tab),
// `scoutStarters` (the Scout tab's suggested prompts) and `panelTabs` (the rail, in order).
//
// Pure, and imported by `node --test`.

import type { PanelTab } from '../sidebar/panelFacts.ts';

export type AccountsPage = 'board' | 'ae' | 'oversight';
export type CompanyInfo = 'strategist' | 'ae' | 'oversight';
export type FactStripKind = 'strategist' | 'ae' | 'oversight';
export type ScoutStarters = 'strategist' | 'ae' | 'oversight';

export interface PanelView {
  companyInfo: CompanyInfo;
  factStrip: FactStripKind;
  scoutStarters: ScoutStarters;
  panelTabs: readonly PanelTab[];
}

/** Where every account link opens (Scout's replies, copied links): the board, which sends an AE on
 * to their own book. */
export const ACCOUNT_LINK_BASE = '/execution';

// The strategist's rail, unchanged. The others drop Outreach: it is the strategist's people,
// sequence and push surface, and the AE's and the oversight panels are read-only.
const STRATEGIST_TABS: readonly PanelTab[] = ['Scout', 'Company', 'Outreach', 'Research', 'History'];
const READ_ONLY_TABS: readonly PanelTab[] = ['Scout', 'Company', 'Research', 'History'];

const PAGE_VIEWS: Record<AccountsPage, PanelView> = {
  board: { companyInfo: 'strategist', factStrip: 'strategist', scoutStarters: 'strategist', panelTabs: STRATEGIST_TABS },
  ae: { companyInfo: 'ae', factStrip: 'ae', scoutStarters: 'ae', panelTabs: READ_ONLY_TABS },
  oversight: { companyInfo: 'oversight', factStrip: 'oversight', scoutStarters: 'oversight', panelTabs: READ_ONLY_TABS },
};

/** The panel settings (Company info, strip, Scout prompts, rail) for the page showing it. */
export function panelForPage(page: AccountsPage): PanelView {
  return PAGE_VIEWS[page];
}

// The panel a role gets off the board (All accounts): unchanged from before go-live's tab merge.
const OFF_BOARD_PANEL: Record<string, AccountsPage> = {
  admin: 'board',
  strategist: 'board',
  leadership: 'oversight',
  viewer: 'oversight',
  ae: 'ae',
};

/** The panel this role gets on All accounts. Unknown, or not yet known: the board's. */
export function offBoardPanel(role: string | null | undefined): AccountsPage {
  return (role && OFF_BOARD_PANEL[role]) || 'board';
}
